// AGC: LotMapPreviewCard - Map preview card showing lot polygon
// AGC: Simple visual closure with polygon preview and navigation to full map
// AGC: Now supports NDVI overlay and management zones from Sentinel-2

import React, { useState } from 'react'
import { MapPin, Maximize2, ZoomIn, ZoomOut, Layers, X, AlertTriangle } from 'lucide-react'
import { MapContainer, TileLayer, Polygon, useMap, ImageOverlay } from 'react-leaflet'
import L from 'leaflet'
import { GlassCard } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import type { LotMapPreviewData, ManagementZone } from '../../types'
import 'leaflet/dist/leaflet.css'

// AGC: Zone data with GeoJSON for map rendering
interface ZoneWithGeoJSON extends ManagementZone {
  geoJSON?: GeoJSON.Polygon | GeoJSON.MultiPolygon
}

interface LotMapPreviewCardProps {
  mapData: LotMapPreviewData
  onPress: () => void
  ndviOverlayUrl?: string | null
  showNdviOverlay?: boolean
  onToggleNdviOverlay?: () => void
  // AGC: Zone polygons for management zone display
  zones?: ZoneWithGeoJSON[] | null
  showZones?: boolean
  onToggleZones?: () => void
  // AGC: [FIX] Fullscreen mode - renders without card wrapper
  isFullscreen?: boolean
  // AGC: [FIX] Loading state for satellite data
  isLoadingLayers?: boolean
  // AGC: NDVI fetch error flag
  ndviError?: boolean
}

/**
 * AGC: Zoom controls component for the map
 */
const ZoomControls: React.FC = () => {
  const map = useMap()
  
  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation()
    map.zoomIn()
  }
  
  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation()
    map.zoomOut()
  }
  
  return (
    <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-1">
      <button
        onClick={handleZoomIn}
        className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        aria-label="Zoom in"
      >
        <ZoomIn className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={handleZoomOut}
        className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        aria-label="Zoom out"
      >
        <ZoomOut className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
    </div>
  )
}

/**
 * AGC: Zone Legend Overlay - Shows zone summary on map
 * AGC: Uses a clean legend display instead of potentially broken zone polygons
 */
interface ZoneLegendOverlayProps {
  zones: ZoneWithGeoJSON[]
  bounds: L.LatLngBounds | null
}

const ZoneLegendOverlay: React.FC<ZoneLegendOverlayProps> = ({ zones }) => {
  const { t } = useTranslation()
  if (!zones || zones.length === 0) return null

  // AGC: Sort zones by NDVI (high to low for display)
  const sortedZones = [...zones].sort((a, b) => {
    const aNdvi = a.meanNdvi ?? 0
    const bNdvi = b.meanNdvi ?? 0
    return bNdvi - aNdvi
  })

  return (
    <div
      className="absolute top-2 left-2 z-[1000] bg-white/90 dark:bg-gray-900/90 rounded-lg shadow-lg p-2 text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      {/* AGC: Zone legend title uses i18n key */}
      <div className="font-semibold text-gray-800 dark:text-white mb-1 text-[10px]">
        {t('monitoring.managementZones')}
      </div>
      {sortedZones.map((zone, index) => (
        <div key={zone.id ?? index} className="flex items-center gap-1.5 py-0.5">
          <div 
            className="w-3 h-3 rounded-sm flex-shrink-0"
            style={{ backgroundColor: zone.color }}
          />
          <span className="text-gray-700 dark:text-gray-300 text-[10px]">
            {zone.label}: {zone.percentage?.toFixed(0) ?? '--'}%
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * AGC: Calculate center and bounds from polygon coordinates
 */
const calculateBounds = (polygon: [number, number][]) => {
  if (!polygon || polygon.length === 0) {
    return {
      center: [-15.793889, -47.882778] as [number, number], // AGC: Default to Brasília
      zoom: 15,
    }
  }
  
  let minLat = polygon[0][0]
  let maxLat = polygon[0][0]
  let minLng = polygon[0][1]
  let maxLng = polygon[0][1]
  
  polygon.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  })
  
  const center: [number, number] = [
    (minLat + maxLat) / 2,
    (minLng + maxLng) / 2,
  ]
  
  // AGC: Calculate appropriate zoom based on polygon size
  const latDiff = maxLat - minLat
  const lngDiff = maxLng - minLng
  const maxDiff = Math.max(latDiff, lngDiff)
  
  let zoom = 15
  if (maxDiff > 0.1) zoom = 12
  else if (maxDiff > 0.05) zoom = 13
  else if (maxDiff > 0.02) zoom = 14
  else if (maxDiff > 0.01) zoom = 15
  else zoom = 16
  
  return { center, zoom }
}

/**
 * AGC: LotMapPreviewCard - Map preview component
 */
export const LotMapPreviewCard: React.FC<LotMapPreviewCardProps> = ({
  mapData,
  onPress,
  ndviOverlayUrl,
  showNdviOverlay = false,
  onToggleNdviOverlay,
  zones,
  showZones = false,
  onToggleZones,
  isFullscreen = false,
  isLoadingLayers = false,
  ndviError = false,
}) => {
  const { t } = useTranslation()
  const { center, zoom } = calculateBounds(mapData.polygon)
  const [ndviOpacity, setNdviOpacity] = useState(0.7)
  // AGC: [FIX] State for satellite vs standard map
  const [useSatellite, setUseSatellite] = useState(true)
  // AGC: [FIX] State for lot boundary visibility
  const [showBoundary, setShowBoundary] = useState(true)
  
  // AGC: Check if we have valid polygon data
  const hasValidPolygon = mapData.polygon && mapData.polygon.length >= 3
  
  // AGC: Calculate bounds for NDVI overlay
  const overlayBounds = hasValidPolygon && mapData.bounds 
    ? L.latLngBounds(
        [mapData.bounds.southWest.lat, mapData.bounds.southWest.lng],
        [mapData.bounds.northEast.lat, mapData.bounds.northEast.lng]
      )
    : null
    
  // AGC: Check if we have zones with GeoJSON
  const hasZonesGeoJSON = zones && zones.length > 0 && zones.some(z => z.geoJSON)
  
  // AGC: [FIX] In fullscreen mode, render without GlassCard wrapper
  if (isFullscreen) {
    return (
      <div className="h-full w-full relative">
        {hasValidPolygon ? (
          <MapContainer
            center={center}
            zoom={zoom - 1}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            dragging={true}
            scrollWheelZoom={true}
          >
            {/* AGC: [FIX] Toggle between satellite and standard map */}
            {useSatellite ? (
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
              />
            ) : (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
            )}
            
            {/* AGC: [FIX] Conditional lot boundary polygon */}
            {showBoundary && (
              <Polygon
                positions={mapData.polygon.map(([lat, lng]) => [lat, lng])}
                pathOptions={{
                  color: '#10B981',
                  fillColor: '#10B981',
                  fillOpacity: 0.2,
                  weight: 3,
                }}
              />
            )}
            
            {/* AGC: NDVI overlay in fullscreen */}
            {showNdviOverlay && ndviOverlayUrl && overlayBounds && (
              <ImageOverlay
                url={ndviOverlayUrl}
                bounds={overlayBounds}
                opacity={ndviOpacity}
              />
            )}
            
            {/* AGC: Zone Legend overlay when zones are shown */}
            {showZones && zones && zones.length > 0 && (
              <ZoneLegendOverlay zones={zones} bounds={overlayBounds} />
            )}
            
            <ZoomControls />
          </MapContainer>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800">
            <MapPin className="w-8 h-8 text-gray-400" />
          </div>
        )}
        
        {/* AGC: Layer controls panel - bottom left */}
        <div className="absolute bottom-20 left-4 z-[1000] bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-lg p-3 space-y-2 min-w-[180px]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('lotPage.layers')}
            </p>
            {isLoadingLayers && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <div className="w-3 h-3 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            )}
          </div>
          
          {/* AGC: [FIX] Satellite/Standard Map Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setUseSatellite(!useSatellite)
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              useSatellite 
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              useSatellite 
                ? 'bg-purple-500 border-purple-500' 
                : 'border-gray-400 dark:border-gray-500'
            }`}>
              {useSatellite && <span className="text-white text-xs">✓</span>}
            </div>
            <span className="text-sm font-medium">{t('lotPage.satelliteBase')}</span>
          </button>
          
          {/* AGC: [FIX] Lot Boundary Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowBoundary(!showBoundary)
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              showBoundary 
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              showBoundary 
                ? 'bg-emerald-500 border-emerald-500' 
                : 'border-gray-400 dark:border-gray-500'
            }`}>
              {showBoundary && <span className="text-white text-xs">✓</span>}
            </div>
            <span className="text-sm font-medium">{t('lotPage.lotBoundary')}</span>
          </button>
          
          {/* AGC: NDVI Layer Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (onToggleNdviOverlay) onToggleNdviOverlay()
            }}
            disabled={!ndviOverlayUrl && !isLoadingLayers}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              !ndviOverlayUrl && !isLoadingLayers
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400'
                : showNdviOverlay
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              showNdviOverlay && ndviOverlayUrl
                ? 'bg-emerald-500 border-emerald-500'
                : ndviError
                  ? 'border-red-400 bg-red-100'
                  : 'border-gray-400 dark:border-gray-500'
            }`}>
              {showNdviOverlay && ndviOverlayUrl && <span className="text-white text-xs">✓</span>}
              {ndviError && <AlertTriangle className="w-3 h-3 text-red-500" />}
            </div>
            <span className="text-sm font-medium">NDVI</span>
            {ndviOverlayUrl && (
              <span className="ml-auto text-xs text-emerald-600 dark:text-emerald-400">●</span>
            )}
            {ndviError && !isLoadingLayers && (
              <span className="ml-auto text-xs text-red-500">{t('lotPage.ndviError')}</span>
            )}
            {isLoadingLayers && !ndviOverlayUrl && (
              <span className="ml-auto text-xs text-gray-400">{t('lotPage.loadingLayers')}</span>
            )}
          </button>
          
          {/* AGC: Zones Layer Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (onToggleZones) onToggleZones()
            }}
            disabled={(!zones || zones.length === 0) && !isLoadingLayers}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              (!zones || zones.length === 0) && !isLoadingLayers
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400'
                : showZones 
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              showZones && zones && zones.length > 0
                ? 'bg-blue-500 border-blue-500' 
                : 'border-gray-400 dark:border-gray-500'
            }`}>
              {showZones && zones && zones.length > 0 && <span className="text-white text-xs">✓</span>}
            </div>
            <span className="text-sm font-medium">{t('monitoring.managementZones')}</span>
            {zones && zones.length > 0 && (
              <span className="ml-auto text-xs text-blue-600 dark:text-blue-400">{zones.length}</span>
            )}
            {isLoadingLayers && (!zones || zones.length === 0) && (
              <span className="ml-auto text-xs text-gray-400">{t('lotPage.loadingLayers')}</span>
            )}
          </button>
          
          {/* AGC: NDVI Opacity slider when NDVI is active */}
          {showNdviOverlay && ndviOverlayUrl && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('lotPage.ndviOpacity')}</span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{Math.round(ndviOpacity * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={ndviOpacity * 100}
                onChange={(e) => setNdviOpacity(parseInt(e.target.value) / 100)}
                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
            </div>
          )}
        </div>
      </div>
    )
  }
  
  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* AGC: Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('lotPage.lotLocation')}
          </h3>
        </div>
        <button
          onClick={onPress}
          className="p-2 rounded-lg bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors border border-slate-200 dark:border-slate-600"
          aria-label={t('lotPage.viewFullMap')}
        >
          <Maximize2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>
      </div>
      
      {/* AGC: Map container */}
      <div 
        className="h-48 relative cursor-pointer"
        onClick={onPress}
      >
        {hasValidPolygon ? (
          <MapContainer
            center={center}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            dragging={true}
            scrollWheelZoom={false}
            doubleClickZoom={false}
            touchZoom={true}
          >
            {/* AGC: Satellite imagery layer */}
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
            
            {/* AGC: Management Zone Markers (show zone colors in legend style) */}
            {showZones && zones && (
              <ZoneLegendOverlay zones={zones} bounds={overlayBounds} />
            )}
            
            {/* AGC: NDVI Overlay (shows colored vegetation - red/orange/green) */}
            {/* AGC: Show when zones are enabled (to display zone colors) OR when NDVI toggle is on */}
            {(showNdviOverlay || showZones) && ndviOverlayUrl && overlayBounds && (
              <ImageOverlay
                url={ndviOverlayUrl}
                bounds={overlayBounds}
                opacity={ndviOpacity}
                zIndex={500}
              />
            )}
            
            {/* AGC: Lot polygon */}
            <Polygon
              positions={mapData.polygon}
              pathOptions={{
                color: (showNdviOverlay || showZones) ? '#FFFFFF' : '#F59E0B',
                fillColor: (showNdviOverlay || showZones) ? 'transparent' : '#FCD34D',
                fillOpacity: (showNdviOverlay || showZones) ? 0 : 0.3,
                weight: (showNdviOverlay || showZones) ? 2 : 3,
              }}
            />
            
            {/* AGC: Zoom controls */}
            <ZoomControls />
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('lotPage.noPolygonData')}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* AGC: Footer with area and overlay toggles */}
      <div className="p-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-600 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {mapData.totalHectares.toFixed(1)} ha
        </span>
        <div className="flex items-center gap-2">
          {/* AGC: Zones toggle button */}
          {hasZonesGeoJSON && onToggleZones && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleZones()
              }}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                showZones
                  ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                  : 'bg-slate-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
              title={showZones ? t('lotPage.hideZones') : t('lotPage.showZones')}
            >
              {showZones ? (
                <X className="w-3 h-3" />
              ) : (
                <Layers className="w-3 h-3" />
              )}
              <span>{t('lotPage.zones')}</span>
            </button>
          )}
          {/* AGC: NDVI toggle button */}
          {ndviOverlayUrl && onToggleNdviOverlay && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleNdviOverlay()
              }}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                showNdviOverlay
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-slate-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
              title={showNdviOverlay ? t('lotPage.hideNdvi') : t('lotPage.showNdvi')}
            >
              {showNdviOverlay ? (
                <X className="w-3 h-3" />
              ) : (
                <Layers className="w-3 h-3" />
              )}
              <span>NDVI</span>
            </button>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('lotPage.tapToViewMap')}
          </span>
        </div>
      </div>
    </div>
  )
}

export default LotMapPreviewCard




